import Config from "config";

/**
 * Wait for `google.maps.Geocoder` — either the map already loaded the script
 * (GoogleMapReact) or we inject the same script once. Do not use axios/fetch to
 * `geocode/json` from the browser: CORS + custom headers often break; the JS SDK does not.
 */
function ensureMapsApi(apiKey: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("no window"));
      return;
    }
    const w = window as Window & { google?: { maps?: { Geocoder?: unknown } } };
    if (w.google?.maps?.Geocoder) {
      resolve();
      return;
    }

    let interval: ReturnType<typeof setInterval> | undefined;
    const deadline = Date.now() + 25000;

    const finish = () => {
      if (interval) clearInterval(interval);
    };

    const tryReady = (): boolean => {
      if (w.google?.maps?.Geocoder) {
        finish();
        resolve();
        return true;
      }
      if (Date.now() > deadline) {
        finish();
        reject(new Error("Google Maps API load timeout"));
        return true;
      }
      return false;
    };

    if (tryReady()) return;

    interval = setInterval(() => tryReady(), 50);

    const already = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
    if (!already) {
      const s = document.createElement("script");
      s.async = true;
      s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
      s.onerror = () => {
        finish();
        reject(new Error("Google Maps script failed to load"));
      };
      document.head.appendChild(s);
    }
  });
}

/** Dedupe key: ignore spaces/case so "M5E 1A4" and "M5E1A4" match. */
export function postalDedupeKey(z: string): string {
  return z.replace(/\s+/g, "").toUpperCase();
}

/** First occurrence wins; order preserved. */
export function dedupePostalStrings(zips: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const z of zips) {
    if (!z?.trim()) continue;
    const trimmed = z.trim();
    const key = postalDedupeKey(trimmed);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
}

function extractPostalFromFormatted(formatted: string): string | null {
  if (!formatted) return null;
  const ca = formatted.match(/\b([A-Za-z]\d[A-Za-z][\s-]?\d[A-Za-z]\d)\b/);
  if (ca) return ca[1].replace(/\s+/g, "").toUpperCase();
  const us = formatted.match(/\b(\d{5})(?:-(\d{4}))?\b/);
  if (us) return us[1] + (us[2] ? `-${us[2]}` : "");
  return null;
}

/** Minimal shape from `google.maps.Geocoder` results (no @types/google.maps required). */
export type GeocoderResultLite = {
  address_components?: { long_name?: string; short_name?: string; types?: string[] }[];
  formatted_address?: string;
};

function postalFromGeocodeResult(result: GeocoderResultLite): string | null {
  for (const comp of result.address_components || []) {
    if (Array.isArray(comp.types) && comp.types.includes("postal_code")) {
      const pc = comp.long_name || comp.short_name;
      if (typeof pc === "string" && pc.trim()) return pc.trim();
    }
  }
  if (result.formatted_address) {
    return extractPostalFromFormatted(result.formatted_address);
  }
  return null;
}

/**
 * Reverse geocode via Maps JavaScript Geocoder (`REACT_APP_GOOGLE_MAPS_API_KEY`).
 * Used when `/api/locations/zip-codes` returns nothing. Avoids browser CORS on REST geocode/json.
 */
export async function reverseGeocodeWithMapsKey(lat: number, lng: number): Promise<string | null> {
  const key = Config.GOOGLE_MAPS_API_KEY?.trim();
  if (!key) return null;

  try {
    await ensureMapsApi(key);
    const g = (window as { google?: { maps: any } }).google;
    if (!g?.maps?.Geocoder) return null;

    const geocoder = new g.maps.Geocoder();
    const results = await new Promise<GeocoderResultLite[] | null>((resolve) => {
      geocoder.geocode({ location: { lat, lng } }, (res: GeocoderResultLite[] | null, status: string) => {
        const OK = g.maps.GeocoderStatus?.OK ?? "OK";
        if (status !== OK || !res?.length) {
          resolve(null);
          return;
        }
        resolve(res);
      });
    });

    if (!results?.length) return null;
    for (const r of results) {
      const pc = postalFromGeocodeResult(r);
      if (pc) return pc;
    }
    return null;
  } catch {
    return null;
  }
}

/** Same geometry as api/controllers/locations `samplePointsWithinRadius` (centre + ring). */
export function samplePointsWithinRadius(
  latNum: number,
  lngNum: number,
  radiusMiles: number,
  count: number
): { lat: number; lng: number }[] {
  const milesPerDegLat = 69.11;
  const cosLat = Math.cos((latNum * Math.PI) / 180);
  const points: { lat: number; lng: number }[] = [{ lat: latNum, lng: lngNum }];
  const ringPoints = Math.max(0, count - 1);
  for (let i = 0; i < ringPoints; i++) {
    const theta = (2 * Math.PI * i) / ringPoints;
    const dLat = (radiusMiles * Math.cos(theta)) / milesPerDegLat;
    const dLng = (radiusMiles * Math.sin(theta)) / (milesPerDegLat * cosLat);
    points.push({ lat: latNum + dLat, lng: lngNum + dLng });
  }
  return points;
}

/**
 * When GET /api/locations/nearby-zips returns [], sample the same way in-browser via Maps Geocoder
 * so first load can still send a multi-zip `zip=` to /api/stores/me.
 */
export async function collectNearbyPostalCodesClient(
  lat: number,
  lng: number,
  options: { radiusMiles: number; sampleCount: number }
): Promise<string[]> {
  const key = Config.GOOGLE_MAPS_API_KEY?.trim();
  if (!key) return [];

  const { radiusMiles, sampleCount } = options;
  const points = samplePointsWithinRadius(lat, lng, radiusMiles, sampleCount);
  const seenKeys = new Set<string>();
  const ordered: string[] = [];
  const batchSize = 5;

  try {
    await ensureMapsApi(key);
  } catch {
    return [];
  }

  for (let i = 0; i < points.length; i += batchSize) {
    const batch = points.slice(i, i + batchSize);
    const results = await Promise.all(batch.map((p) => reverseGeocodeWithMapsKey(p.lat, p.lng)));
    for (const z of results) {
      if (!z?.trim()) continue;
      const trimmed = z.trim();
      const key = postalDedupeKey(trimmed);
      if (seenKeys.has(key)) continue;
      seenKeys.add(key);
      ordered.push(trimmed);
    }
  }
  return ordered;
}
